import React from 'react';
import { Button, List, Modal, Spin, Tag, Typography } from 'antd';
import { CheckCircleOutlined, LinkOutlined, PlusOutlined } from '@ant-design/icons';
import type { RefSelectProps } from 'antd/lib/select';
import { VendorsSearch } from 'components/atoms/VendorsSearch';
import type { SimKeyMatchCandidate } from 'api/vendor';
import type { MapVendorModalProps, VendorSearchOption } from '../types';

/** Color and label mapping for sim_key match confidence levels. */
const CONFIDENCE_CONFIG: Record<string, { color: string; label: string }> = {
  exact: { color: 'green', label: 'High Confidence' },
  high: { color: 'green', label: 'High Confidence' },
  medium: { color: 'orange', label: 'Medium Confidence' },
  low: { color: 'orange', label: 'Low Confidence' }
};

/**
 * Modal dialog for searching and selecting an existing vendor to map to a supplier.
 *
 * Layout:
 * 1. **Auto-Suggestion Section** — sim_key-based exact and fuzzy matches (Paths 1 & 2)
 * 2. **Manual Search Section** — free-text VendorsSearch fallback
 * 3. **Footer** — "Create New Vendor" and "Close" buttons
 * @param {MapVendorModalProps} props - Component props.
 * @returns {React.ReactElement} The map-vendor modal.
 */
const MapVendorModal: React.FC<MapVendorModalProps> = ({
  open,
  activeSupplier,
  sessionId,
  searchRef,
  defaultOptions,
  onClose,
  onSelectVendor,
  onCreateNew,
  searchFn,
  simKeySearchLoading,
  simKeyResults,
  onConfirmSimKeyMatch,
  queueProgress,
  onSkip
}) => {
  const vendorTarget = activeSupplier?.supplierName ?? '';
  const hasExact = !!simKeyResults?.exactMatch;
  const hasFuzzy = (simKeyResults?.fuzzyMatches?.length ?? 0) > 0;
  const hasAnyMatch = hasExact || hasFuzzy;

  return (
    <Modal
      title={`Map Vendor to Supplier${queueProgress ? ` (${queueProgress})` : ''}`}
      open={open}
      destroyOnClose
      onCancel={onClose}
      width={560}
      footer={[
        onSkip && (
          <Button key="skip" onClick={onSkip}>
            Skip
          </Button>
        ),
        <Button key="create" type="default" icon={<PlusOutlined />} onClick={onCreateNew}>
          Create New Vendor
        </Button>,
        <Button key="close" onClick={onClose}>
          Close
        </Button>
      ].filter(Boolean)}
    >
      <Typography.Paragraph className="mb-4">
        Search for a vendor to map to <Typography.Text strong>{vendorTarget}</Typography.Text>
      </Typography.Paragraph>

      {/* ── Auto-Suggestion Section ─────────────────────────────────────── */}
      {simKeySearchLoading && (
        <div className="mb-4 text-center py-4">
          <Spin size="default" />
          <Typography.Text className="ml-2 text-gray-500">Searching for matches...</Typography.Text>
        </div>
      )}

      {!simKeySearchLoading && hasExact && simKeyResults?.exactMatch && (
        <div className="mb-4 p-3 border border-green-300 bg-green-50 rounded">
          <Typography.Text strong className="block mb-2">
            <CheckCircleOutlined className="mr-1 text-green-600" />
            High-confidence match found
          </Typography.Text>
          <div className="flex justify-between items-center">
            <div>
              <Typography.Text>{simKeyResults.exactMatch.name}</Typography.Text>
              {simKeyResults.exactMatch.domain && (
                <Typography.Text className="ml-2 text-gray-500">{simKeyResults.exactMatch.domain}</Typography.Text>
              )}
            </div>
            <Button type="primary" size="small" onClick={() => onConfirmSimKeyMatch(simKeyResults.exactMatch!)}>
              Confirm Match
            </Button>
          </div>
        </div>
      )}

      {!simKeySearchLoading && hasFuzzy && (
        <div className="mb-4">
          <Typography.Text strong className="block mb-2">
            Similar vendors
          </Typography.Text>
          <div className="max-h-72 overflow-y-auto">
            <List
              size="small"
              bordered
              dataSource={simKeyResults!.fuzzyMatches}
              renderItem={(candidate: SimKeyMatchCandidate) => (
                <List.Item
                  actions={[
                    <Button
                      key="select"
                      type="default"
                      size="small"
                      icon={<LinkOutlined />}
                      onClick={() => onConfirmSimKeyMatch(candidate)}
                    >
                      Select
                    </Button>
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <span>
                        {candidate.name}
                        <Tag className="ml-2" color={CONFIDENCE_CONFIG[candidate.confidence]?.color ?? 'default'}>
                          {CONFIDENCE_CONFIG[candidate.confidence]?.label ?? candidate.confidence}
                        </Tag>
                      </span>
                    }
                    description={candidate.domain || undefined}
                  />
                </List.Item>
              )}
            />
          </div>
        </div>
      )}

      {!simKeySearchLoading && !hasAnyMatch && simKeyResults && (
        <Typography.Paragraph className="mb-4 text-gray-500">
          No similar vendors found by name matching. Try a manual search below.
        </Typography.Paragraph>
      )}

      {/* ── Manual Search Section ───────────────────────────────────────── */}
      {!simKeySearchLoading && (
        <>
          <Typography.Text strong className="block mb-2">
            {hasAnyMatch ? 'Or search manually' : 'Search vendors'}
          </Typography.Text>
          <VendorsSearch
            ref={searchRef}
            key={`map-vendor-search-${sessionId}`}
            width="100%"
            placeholder="Search by vendor name, domain, or ID..."
            defaultOptions={defaultOptions}
            onChange={onSelectVendor}
            searchFn={searchFn}
          />
        </>
      )}
    </Modal>
  );
};

MapVendorModal.displayName = 'MapVendorModal';

export default React.memo(MapVendorModal);
